/**
 * React Error Boundary
 * Silently catches React component errors and reports to Crashlytics
 * Prevents white screen of death by showing last working UI
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import { crashlytics } from "../services/crashlytics";
import { CURRENT_ENV } from "../services/api";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 * Wraps your app to catch React component errors
 *
 * Usage:
 * <ErrorBoundary>
 *   <YourApp />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so next render shows fallback UI
    return {
      hasError: true,
      error,
    };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo): Promise<void> {
    // Only report in production
    if (CURRENT_ENV.includes("production")) {
      try {
        // Log error details
        await crashlytics.log(
          `React Error Boundary caught error: ${error.message}`
        );

        // Add component stack as custom key
        await crashlytics.setCustomKey(
          "component_stack",
          errorInfo.componentStack || "unknown"
        );
        await crashlytics.setCustomKey("error_boundary", "true");

        // Record the exception
        await crashlytics.recordException(error, {
          type: "react_error_boundary",
          component_stack: errorInfo.componentStack,
        });

        console.log("📊 React error reported to Crashlytics silently");
      } catch (reportingError) {
        console.error("Failed to report React error:", reportingError);
      }
    } else {
      // In development, log to console for debugging
      console.error("🔥 React Error Boundary caught error:", error);
      console.error("Component stack:", errorInfo.componentStack);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Show fallback UI if provided, otherwise show nothing (keeps last UI visible)
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Minimal fallback - invisible to user in most cases
      // The error is already logged, user can continue using other parts of app
      return null;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
